import React, { useState } from 'react';
import {
  PROXY_API_BASE as AUTH_PROXY_API_BASE, PROXY_API_TOKEN,
  getCachedProxyCredential, refreshProxyCredential,
} from '../api/proxyAuth';
import { webUsbSupported } from '../utils/webAdbTap';
import DeviceIdChips from './DeviceIdChips';
import { buildTapCommand, resolveTapCredential, reuseOrMintEnrollment, tapIsUnowned } from '../utils/tapCommand';
import { STATIC_TOKEN_TAP_COPY } from '../utils/isolationCopy';

const ENROLLMENT_PLACEHOLDER = '<enrollment-id>:<secret>';
import {
  useWebUsbCapture, startCapture, stopCapture, isActive, getDeviceName, setDeviceName as saveDeviceName,
} from '../utils/webUsbCapture';

// Device capture panel — shared by ConfigPage and the Connect Device dialog.
// Android production builds can't be MITM'd; the operator runs a CLI tap on
// their Mac and this card assembles the command. The token goes ONLY to the
// clipboard — never rendered. In Chrome/Edge the same tap also runs in the
// page over WebUSB: one shared session (utils/webUsbCapture.js) that the
// homepage toolbar button drives too.

const TAP_API_URL = AUTH_PROXY_API_BASE || 'http://localhost:8001';

// Mint a long-lived per-user enrollment for a USB tap (proxy POST /proxy/enroll,
// platform 'logcat'; the secret is returned once). null on any failure (older proxy,
// machine caller, network) so the caller falls back to a token and warns.
async function mintLogcatEnrollment(deviceLabel) {
  try {
    const r = await fetch(`${TAP_API_URL}/proxy/enroll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform: 'logcat', deviceLabel }),
    });
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

// The caller's enrollments (viewer-scoped, never carries a secret). null on any failure.
async function listEnrollments() {
  try {
    const r = await fetch(`${TAP_API_URL}/proxy/enrollments`);
    if (!r.ok) return null;
    const d = await r.json();
    return d && Array.isArray(d.enrollments) ? d.enrollments : null;
  } catch { return null; }
}

function tapStorage() {
  try { return window.sessionStorage; } catch { return null; }
}

const CAPTURE_MODE_KEY = 'fx_capture_mode';
function readCaptureMode() {
  try {
    const v = localStorage.getItem(CAPTURE_MODE_KEY);
    return v === 'adb' ? 'adb' : 'mitm';
  } catch { return 'mitm'; }
}

export default function DeviceCapturePanel() {
  const [mode, setModeState] = useState(readCaptureMode);
  const setMode = (m) => {
    setModeState(m);
    try { localStorage.setItem(CAPTURE_MODE_KEY, m); } catch { /* ignore */ }
  };
  const [deviceName, setDeviceNameState] = useState(getDeviceName);
  const setDeviceName = (v) => { setDeviceNameState(v); saveDeviceName(v); };
  const [copied, setCopied] = useState(false);
  const [usedPlaceholder, setUsedPlaceholder] = useState(false);
  const [credKind, setCredKind] = useState(''); // kind of credential in the last copied command
  const [copyErr, setCopyErr] = useState('');
  const capture = useWebUsbCapture();
  const usb = { ...capture, active: isActive(capture), start: startCapture, stop: stopCapture };

  const safeName = (deviceName.trim() || 'android-prod').replace(/[^\w.-]/g, '-');
  // Display always shows placeholders, never a real secret.
  const displayCmd = buildTapCommand({ api: TAP_API_URL, deviceName: safeName, credential: { kind: 'enrollment', value: ENROLLMENT_PLACEHOLDER } });

  async function copy() {
    setCopyErr('');
    // Per-user enrollment first (rows are owned by this user); the shared static token
    // is the last resort and the card then says its rows will not be assigned to anyone.
    const credential = await resolveTapCredential({
      mintEnrollment: () => reuseOrMintEnrollment({
        storage: tapStorage(), listEnrollments, mint: () => mintLogcatEnrollment(safeName), deviceLabel: safeName,
      }),
      getJwt: getCachedProxyCredential,
      refreshJwt: () => refreshProxyCredential(),
      staticToken: PROXY_API_TOKEN,
    });
    try {
      await navigator.clipboard.writeText(buildTapCommand({ api: TAP_API_URL, deviceName: safeName, credential }));
      setUsedPlaceholder(credential.kind === 'none');
      setCredKind(credential.kind);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyErr('Clipboard unavailable — copy the command manually.');
    }
  }

  return (
    <div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {[['mitm', 'Wi-Fi proxy (MITM)'], ['adb', 'USB adb (production builds)']].map(([id, label]) => (
            <button key={id} onClick={() => setMode(id)}
              style={{ fontSize: 12, padding: '6px 14px', borderRadius: 7, cursor: 'pointer', fontWeight: 700,
                border: mode === id ? 'none' : '1px solid var(--line)',
                background: mode === id ? 'var(--primary)' : 'var(--bg)',
                color: mode === id ? '#fff' : 'inherit' }}>
              {label}
            </button>
          ))}
        </div>
        {mode === 'mitm' && (
          <ol style={{ fontSize: 13, color: 'var(--muted)', margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
            <li>Point the device's Wi-Fi proxy at this proxy's host, port <b>8000</b>.</li>
            <li>Install and trust the proxy CA certificate on the device (the QR enrollment flow can do this for iOS).</li>
            <li>Debug/QA builds trust the user CA; Android production builds do NOT — use the USB adb mode for those.</li>
          </ol>
        )}
        {mode === 'adb' && (<>
        <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' }}>
          Production Android builds can't be MITM'd; this streams GAM ad request URLs from adb logcat into the dashboard. Turn on USB debugging on the phone and plug it into this computer.
        </p>
        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Device name</label>
        <input
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder="android-prod"
          disabled={usb.active}
          style={{ fontSize: 13, padding: '6px 10px', borderRadius: 7, border: '1px solid var(--line)', background: 'var(--bg)', color: 'inherit', width: 240, marginBottom: 12 }}
        />
        <WebUsbSection usb={usb} deviceName={safeName} />
        <div style={{ fontSize: 12, fontWeight: 600, margin: '16px 0 6px' }}>
          {webUsbSupported() ? 'Or run it from a terminal' : 'Run it from a terminal'}
          <span style={{ fontWeight: 400, color: 'var(--muted)' }}> (needs adb + the repo on this machine)</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', flexWrap: 'wrap' }}>
          <pre style={{ flex: '1 1 320px', margin: 0, padding: '8px 12px', fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 7, overflowX: 'auto', whiteSpace: 'pre' }}>
            {displayCmd}
          </pre>
          <button onClick={copy}
            style={{ fontSize: 12, padding: '6px 18px', borderRadius: 7, border: 'none', background: copied ? 'var(--ok, #2e7d32)' : 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            {copied ? 'Copied ✓' : 'Copy command'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
          {credKind === 'enrollment' && 'The copied command carries a personal enrollment for this tap (reused on later copies in this tab while it is still active; a new one is minted only if it ended or the tab was closed). Rows it captures are assigned to your account.'}
          {credKind === 'jwt' && 'Enrollment was unavailable, so the copied command carries your current proxy credential, which expires in about 1 hour — re-copy for a new session.'}
          {!credKind && 'Copy command mints a personal enrollment for this tap, so its rows are assigned to your account.'}
          {usedPlaceholder && ' No credential was available, so the clipboard has the literal <API_TOKEN> placeholder — substitute the proxy API token from the droplet .env.'}
        </div>
        {tapIsUnowned(credKind ? { kind: credKind } : null) && credKind && (
          <div data-testid="static-token-warning" role="alert"
            style={{ fontSize: 12, marginTop: 6, padding: '6px 10px', borderRadius: 7, background: 'var(--warn-bg)', color: 'var(--ink)', border: '1px solid var(--line)' }}>
            {STATIC_TOKEN_TAP_COPY}
          </div>
        )}
        {copyErr && <div style={{ fontSize: 11, color: 'var(--error, #c62828)', marginTop: 4 }}>{copyErr}</div>}
        </>)}
    </div>
  );
}

function WebUsbSection({ usb, deviceName }) {
  if (!webUsbSupported()) {
    return (
      <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 12px', border: '1px dashed var(--line)', borderRadius: 7 }}>
        Capturing straight from the browser needs Chrome or Edge on a desktop (WebUSB). This browser can't, so use the terminal command below.
      </div>
    );
  }
  const { phase, detail, stats, device } = usb;
  const btn = (label, onClick, bg) => (
    <button onClick={onClick}
      style={{ fontSize: 12, padding: '6px 18px', borderRadius: 7, border: 'none', background: bg, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
      {label}
    </button>
  );
  return (
    <div style={{ padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 7, background: 'var(--bg)' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {usb.active
          ? btn(phase === 'running' ? 'Stop capture' : 'Cancel', usb.stop, 'var(--error, #c62828)')
          : btn('Connect phone & capture', () => usb.start(deviceName), 'var(--primary)')}
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          {phase === 'idle' && 'Capture in this browser'}
          {phase === 'selecting' && 'Pick the phone in the browser prompt…'}
          {phase === 'authorizing' && 'Waiting for the phone…'}
          {phase === 'running' && `Capturing from ${(device && device.name) || 'phone'} as "${device && device.deviceName}"`}
          {phase === 'stopped' && 'Capture stopped'}
          {phase === 'error' && 'Could not capture'}
        </span>
      </div>
      {detail && (
        <div style={{ fontSize: 12, marginTop: 6, color: phase === 'error' ? 'var(--error, #c62828)' : 'var(--muted)' }}>{detail}</div>
      )}
      {phase === 'running' && (
        <div style={{ marginTop: 6 }}><DeviceIdChips device={device} stats={stats} waitingHint /></div>
      )}
      {stats && (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
          {stats.sent} sent · {stats.seen} ad URLs seen · {stats.deduped} duplicate log copies dropped
          {stats.rejected ? ` · ${stats.rejected} rejected by the proxy` : ''}
          {stats.lastAt ? ` · last ${new Date(stats.lastAt).toLocaleTimeString()}` : ''}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
        Capture keeps running while you move between pages (the homepage toolbar shows it too); closing the tab stops it.
        {' '}USB capture is read-only: IU Rewrite and Chaos rules don't apply to it, because requests go straight from the phone to Google. Use the Wi-Fi proxy for those. If the phone shows as busy, run <code>adb kill-server</code> first, because only one program can hold it.
      </div>
    </div>
  );
}
